import { createAsyncThunk } from '@reduxjs/toolkit'

import type { RootState } from 'redux/store/RootReducer'
import type { AppDispatch } from 'redux/store'
import taListAPI from 'api/ApiActions/TextAnalysis/Contributions'
import taTagsAPI from 'api/ApiActions/TextAnalysis/ContributionTags'
import taSentimentAPI from 'api/ApiActions/TextAnalysis/Sentiment'
import taApplicationDataAPI from 'api/ApiActions/TextAnalysis/ApplicationData'
import { handleStatus500 } from 'api/Utility/HandleStatus500'
import { EXPORT_LIMIT } from 'containers/TextAnalysis/Constants'

const searchParams = new URLSearchParams(window.location.search)

const LOCATION_PATH = window.location.pathname.split('/')
const PROJECT_ID =
  searchParams.get('project_id') || LOCATION_PATH[LOCATION_PATH.length - 1]
const ownerId = searchParams.get('owner_id')
const OWNER_TYPE = searchParams.get('owner_type')

export const fetchContributions: any = createAsyncThunk<
  any,
  Record<string, any> | undefined,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/getContributions',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      textAnalysis: { main, theme },
    } = getState()
    const keyPhrases = theme.selectedTheme?.key_phrases ?? []
    const { queries, userFilters } = main

    const response = await taListAPI.getList({
      ...payload,
      ...queries,
      attributes: {
        'project.id': PROJECT_ID,
        ...(ownerId ? { 'owner.id': ownerId } : {}),
        ...(OWNER_TYPE ? { 'owner.type': OWNER_TYPE } : {}),
        ...userFilters,
        ...(keyPhrases.length > 0 ? { key_phrases: keyPhrases } : {}),
      },
    })
    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const getSingleContribution: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/getSingleContribution',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taListAPI.getList({
      source_id: site.id,
      attributes: {
        'project.id': PROJECT_ID,
        ...payload,
      },
    })
    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const fetchAllTags: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/getTags',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taTagsAPI.getTags({
      ...payload,
      source_id: site.id,
      attributes: {
        'project.id': PROJECT_ID,
      },
    })
    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const newContributionTags: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>('textAnalysis/createTags', async (payload = {}, { rejectWithValue }) => {
  const response = await taTagsAPI.createTags({
    ...payload,
    attributes: {
      'project.id': PROJECT_ID,
    },
  })
  if (response?.status >= 200 && response?.status < 300) {
    return response.data
  } else {
    return rejectWithValue('Error occured')
  }
})

export const updateContributionTags: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>('textAnalysis/updateTags', async (payload = {}, { rejectWithValue }) => {
  const response = await taTagsAPI.updateTags({
    ...payload,
  })
  if (response?.status >= 200 && response?.status < 300) {
    return response.data
  }
  handleStatus500(response, '', 'TA')
  return rejectWithValue('Error occurred')
})

export const bulkTags: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/bulkUpdateTags',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taTagsAPI.bulkUpdateTags({
      ...payload,
      source_id: site.id,
      attributes: {
        'project.id': PROJECT_ID,
      },
    })
    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const editSentiment: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>('textAnalysis/editSentiment', async (payload = {}, { rejectWithValue }) => {
  const response = await taListAPI.editSentiment({
    ...payload,
    attributes: {
      'project.id': PROJECT_ID,
    },
  })
  if (response?.status >= 200 && response?.status < 300) {
    return response.data
  }
  handleStatus500(response, '', 'TA')
  return rejectWithValue('Error occurred')
})

export const sentimentSummary: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/sentimentSummary',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taSentimentAPI.sentimentSummary({
      ...payload,
      source_id: site.id,
      owner_id: ownerId,
    })

    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const exportContributionsData: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/exportData',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      textAnalysis: { main },
      site: { site },
    } = getState()
    const { queries, userFilters } = main

    // Set page to 1 and per_page to 10K
    const updatedQueries = {
      ...queries,
      page: 1,
      per_page: EXPORT_LIMIT,
    }

    // Construct attributes dynamically
    const attributes = {
      'project.id': PROJECT_ID,
      ...(ownerId && { 'owner.id': ownerId }),
      ...(OWNER_TYPE && { 'owner.type': OWNER_TYPE }),
      ...userFilters,
    }

    const response = await taListAPI.exportData({
      ...payload,
      ...updatedQueries,
      source_id: site.id,
      attributes,
    })

    if (!response?.status || response.status < 200 || response.status >= 300) {
      handleStatus500(response, '', 'TA')
      return rejectWithValue('Error occurred')
    }

    // Return success data
    return {
      data: response.data,
      headers: response.headers,
    }
  },
)

export const fetchApplicationId: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/fetchApplicationId',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taApplicationDataAPI.getApplicationId({
      ...payload,
      source_id: site.id,
    })

    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const fetchApplicationConfig: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/fetchApplicationConfig',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taApplicationDataAPI.getApplicationConfig({
      ...payload,
      source_id: site.id,
    })

    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const fetchSourceId: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/fetchSourceId',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taApplicationDataAPI.getSourceId({
      ...payload,
      source_id: site.id,
    })

    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const fetchSoureOrOwnerConfig: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/fetchSoureOrOwnerConfig',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taApplicationDataAPI.getSourceOwnerConfig({
      ...payload,
      site_id: site.id,
      owner_id: ownerId,
    })

    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const postOwnerConfig: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/createOwnerConfig',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taApplicationDataAPI.createOwnerConfig({
      ...payload,
      site_id: site.id,
      owner_id: ownerId,
    })

    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const patchOwnerConfig: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/updateOwnerConfig',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taApplicationDataAPI.updateOwnerConfig({
      ...payload,
      site_id: site.id,
    })

    if (response?.status >= 200 && response?.status < 300) {
      return response.data
    }
    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)

export const backfillSentiment: any = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>(
  'textAnalysis/backfillSentiment',
  async (payload = {}, { getState, rejectWithValue }) => {
    const {
      site: { site },
    } = getState()

    const response = await taSentimentAPI.backfillSentiment({
      ...payload,
      owner_id: ownerId,
      source_id: site.id,
    })

    if (
      (response?.status >= 200 && response?.status < 300) ||
      response?.response?.status === 409
    ) {
      const statusCode = response?.status ?? response?.response?.status
      return { status: statusCode }
    }

    handleStatus500(response, '', 'TA')
    return rejectWithValue('Error occurred')
  },
)
